import React, { useEffect, useState } from 'react';

/**
 * clientOnly(loader) — wrap a browser-only component (anything that touches
 * `window` at MODULE scope, e.g. leaflet/react-leaflet) so it can live on a
 * build-time prerendered route.
 *
 * Why not React.lazy: a lazy component inside <Suspense> is loaded BY the
 * server renderer (renderToPipeableStream waits for it via onAllReady), so
 * leaflet's module-scope `window` access would crash the prerender. This
 * helper instead defers the dynamic import to a mount effect — which never
 * runs on the server AND renders the same fallback on the server and the
 * first client render, keeping React 18 hydration byte-identical.
 *
 * Cost: the wrapped component appears one effect-tick + one chunk-fetch after
 * mount (exactly like today's lazy-route behavior). Pass a `fallback` element
 * sized like the real component to avoid layout shift.
 *
 * @param {() => Promise<{default: React.ComponentType}>} loader dynamic import
 * @param {{fallback?: React.ReactNode}} [options]
 * @returns {React.ComponentType}
 */
export default function clientOnly(loader, { fallback = null } = {}) {
  return function ClientOnlyComponent(props) {
    const [Comp, setComp] = useState(null);

    useEffect(() => {
      let alive = true;
      loader().then(
        (mod) => {
          if (alive) setComp(() => mod.default);
        },
        (err) => {
          // A failed chunk fetch must never crash the page — the component
          // simply stays absent (same posture as a failed lazy route chunk).
          console.warn('[clientOnly] failed to load component:', err);
        }
      );
      return () => {
        alive = false;
      };
    }, []);

    if (!Comp) return fallback;
    return <Comp {...props} />;
  };
}
